import React, { useState, useRef, useEffect } from 'react'
import {
  Upload,
  Link as LinkIcon,
  Image as ImageIcon,
  Trash2,
  Loader2,
  HardDrive,
  Globe,
  RefreshCw,
  AlertCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { uploadProductImage, uploadProductImageFromUrl } from '@/lib/api/products'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { resolveImageUrl } from '@/utils/imageUrl'

export interface ProductImageUploaderProps {
  value?: string | null
  onChange: (url: string) => void
  disabled?: boolean
  error?: string
  className?: string
}

export default function ProductImageUploader({
  value,
  onChange,
  disabled = false,
  error,
  className,
}: ProductImageUploaderProps) {
  const [tab, setTab] = useState<'upload' | 'url'>('upload')
  const [urlInput, setUrlInput] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [isImportingUrl, setIsImportingUrl] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [localPreview, setLocalPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Reset image error when value changes
  useEffect(() => {
    setImageError(false)
    if (!value) {
      setLocalPreview(null)
    }
  }, [value])

  // Cleanup blob object url on unmount or preview change
  useEffect(() => {
    return () => {
      if (localPreview && localPreview.startsWith('blob:')) {
        URL.revokeObjectURL(localPreview)
      }
    }
  }, [localPreview])

  const isLocalStored = Boolean(
    value && (value.startsWith('/storage/') || value.startsWith('/api/storage/'))
  )

  const handleFileSelected = async (file: File) => {
    if (!file) return

    // Limit check: 10MB
    const MAX_SIZE_MB = 10
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`La imagen supera el límite máximo de ${MAX_SIZE_MB}MB`)
      return
    }

    // MIME type check
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato no soportado. Usa JPEG, PNG, WEBP, GIF o SVG')
      return
    }

    try {
      setIsUploading(true)
      const previewUrl = URL.createObjectURL(file)
      setLocalPreview(previewUrl)
      setImageError(false)

      const result = await uploadProductImage(file)
      onChange(result.imageUrl)
      toast.success('Imagen subida y guardada en servidor')
    } catch (err: any) {
      setLocalPreview(null)
      const msg = err.response?.data?.message || err.message || 'Error al subir la imagen'
      toast.error(msg)
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelected(file)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    if (disabled || isUploading || isImportingUrl) return

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelected(file)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!disabled && !isUploading && !isImportingUrl) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleImportFromUrl = async () => {
    const trimmed = urlInput.trim()
    if (!trimmed) {
      toast.error('Ingresa una URL válida de imagen')
      return
    }

    try {
      const parsed = new URL(trimmed)
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        toast.error('La URL debe comenzar con http:// o https://')
        return
      }
    } catch {
      toast.error('URL con formato inválido')
      return
    }

    try {
      setIsImportingUrl(true)
      const result = await uploadProductImageFromUrl(trimmed)
      onChange(result.imageUrl)
      setUrlInput('')
      toast.success('Imagen descargada y guardada en el servidor')
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'No se pudo descargar la imagen remota'
      toast.error(msg)
    } finally {
      setIsImportingUrl(false)
    }
  }

  const handleUseExternalUrl = () => {
    const trimmed = urlInput.trim()
    if (!trimmed) {
      toast.error('Ingresa una URL válida')
      return
    }

    try {
      const parsed = new URL(trimmed)
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        toast.error('La URL debe comenzar con http:// o https://')
        return
      }
    } catch {
      toast.error('URL con formato inválido')
      return
    }

    onChange(trimmed)
    setUrlInput('')
    toast.success('Enlace externo asignado al producto')
  }

  const handleRemoveImage = () => {
    if (localPreview && localPreview.startsWith('blob:')) {
      URL.revokeObjectURL(localPreview)
    }
    setLocalPreview(null)
    onChange('')
    setImageError(false)
    setUrlInput('')
  }

  const displaySrc = localPreview || resolveImageUrl(value)

  return (
    <div className={cn('space-y-2', className)}>
      {/* Current Image View & Actions */}
      {value || localPreview ? (
        <div className="border border-slate-200 bg-white rounded-xl p-3 flex flex-col sm:flex-row gap-3 items-center">
          {/* Thumbnail */}
          <div className="w-20 h-20 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0 relative group">
            {!imageError && displaySrc ? (
              <img
                src={displaySrc}
                alt="Vista previa del producto"
                onError={() => setImageError(true)}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-amber-500 p-1 text-center">
                <AlertCircle className="w-5 h-5 stroke-[1.5]" />
                <span className="text-[9px] mt-0.5 leading-tight text-slate-500">Error carga</span>
              </div>
            )}
          </div>

          {/* Info and Actions */}
          <div className="flex-1 min-w-0 w-full">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {isLocalStored ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <HardDrive className="w-3 h-3 text-emerald-600" />
                  Almacenada en Servidor
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  <Globe className="w-3 h-3 text-blue-600" />
                  Enlace Externo
                </span>
              )}
            </div>

            <p className="text-xs text-slate-500 truncate max-w-full font-mono bg-slate-50 px-2 py-1 rounded border border-slate-100">
              {value}
            </p>

            <div className="flex items-center gap-2 mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled || isUploading || isImportingUrl}
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.click()
                  }
                }}
                className="h-7 text-xs px-2.5 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Cambiar archivo
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled || isUploading || isImportingUrl}
                onClick={handleRemoveImage}
                className="h-7 text-xs px-2.5 rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Quitar
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* Selector Tabs: Upload File vs From URL */
        <div className="border border-slate-200 bg-white rounded-xl overflow-hidden shadow-xs">
          {/* Navigation Tabs */}
          <div className="flex border-b border-slate-100 bg-slate-50/70 p-1 gap-1">
            <button
              type="button"
              onClick={() => setTab('upload')}
              disabled={disabled || isUploading || isImportingUrl}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-medium transition-colors',
                tab === 'upload'
                  ? 'bg-white text-emerald-700 shadow-xs font-semibold'
                  : 'text-slate-500 hover:text-slate-800'
              )}
            >
              <Upload className="w-3.5 h-3.5" />
              Subir Imagen
            </button>
            <button
              type="button"
              onClick={() => setTab('url')}
              disabled={disabled || isUploading || isImportingUrl}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-medium transition-colors',
                tab === 'url'
                  ? 'bg-white text-emerald-700 shadow-xs font-semibold'
                  : 'text-slate-500 hover:text-slate-800'
              )}
            >
              <LinkIcon className="w-3.5 h-3.5" />
              Pegar URL
            </button>
          </div>

          <div className="p-3">
            {tab === 'upload' ? (
              /* Drop / Upload Zone */
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => {
                  if (!disabled && !isUploading && fileInputRef.current) {
                    fileInputRef.current.click()
                  }
                }}
                className={cn(
                  'border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center text-center cursor-pointer transition-all',
                  isDragging
                    ? 'border-emerald-500 bg-emerald-50/50'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/60',
                  (disabled || isUploading) && 'opacity-60 cursor-not-allowed'
                )}
              >
                {isUploading ? (
                  <div className="flex flex-col items-center py-2">
                    <Loader2 className="w-6 h-6 text-emerald-600 animate-spin mb-1.5" />
                    <span className="text-xs font-medium text-slate-700">Subiendo y guardando en servidor...</span>
                    <span className="text-[10px] text-slate-400 mt-0.5">Organizando por vertical y tenant</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-1">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 mb-2">
                      <ImageIcon className="w-5 h-5 text-slate-500" />
                    </div>
                    <span className="text-xs font-medium text-slate-700">
                      Arrastra tu imagen aquí o <span className="text-emerald-700 font-semibold underline">explora</span>
                    </span>
                    <span className="text-[11px] text-slate-400 mt-1">
                      PNG, JPG, WEBP, GIF o SVG (máx. 10MB)
                    </span>
                  </div>
                )}
              </div>
            ) : (
              /* URL Input Section */
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://ejemplo.com/foto-producto.jpg"
                    disabled={disabled || isImportingUrl}
                    className="text-xs h-9 rounded-lg border-slate-200"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleImportFromUrl()
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={handleImportFromUrl}
                    disabled={disabled || isImportingUrl || !urlInput.trim()}
                    className="h-9 px-3 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0 flex items-center gap-1"
                  >
                    {isImportingUrl ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <HardDrive className="w-3.5 h-3.5" />
                        Guardar en Servidor
                      </>
                    )}
                  </Button>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <span className="text-[11px] text-slate-400">
                    Se descargará y guardará particionada por tu vertical.
                  </span>
                  <button
                    type="button"
                    onClick={handleUseExternalUrl}
                    disabled={disabled || isImportingUrl || !urlInput.trim()}
                    className="text-[11px] text-slate-500 hover:text-slate-800 underline disabled:opacity-50"
                  >
                    Usar como URL externa sin guardar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden File Input for Native File Dialog */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}
